import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import PageHero from '@/components/shared/PageHero';
import { publicApi } from '@/lib/api';
import useSEO from '@/hooks/useSEO';
import {
    Camera,
    Play,
    X,
    ChevronLeft,
    ChevronRight,
    Images as ImagesIcon,
    Video as VideoIcon,
} from 'lucide-react';

// Auto-convert watch URLs to embed URLs (fallback if admin pasted a watch link)
const toEmbedUrl = (raw) => {
    if (!raw) return '';
    const url = String(raw).trim();
    const yt1 = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{11})/);
    if (yt1) return `https://www.youtube.com/embed/${yt1[1]}`;
    const yt2 = url.match(/youtube\.com\/shorts\/([\w-]{11})/);
    if (yt2) return `https://www.youtube.com/embed/${yt2[1]}`;
    const vim = url.match(/vimeo\.com\/(\d+)/);
    if (vim) return `https://player.vimeo.com/video/${vim[1]}`;
    return url;
};

export default function GalleryPage() {
    const [albums, setAlbums] = useState([]);
    const [openAlbum, setOpenAlbum] = useState(null);
    const [lightbox, setLightbox] = useState(null); // { photos: [], index }
    const [activeCategory, setActiveCategory] = useState('All');

    useSEO({
        title: 'Gallery',
        description:
            'VVVSI Trust photo & video gallery. Browse event-wise albums from placement drives, career yatras, health camps, education programs, green initiatives and disaster relief across Tamil Nadu and Puducherry.',
    });

    useEffect(() => {
        publicApi.getGalleryAlbums().then(setAlbums).catch(() => {});
    }, []);

    // Unique category list (with All prepended)
    const categories = useMemo(() => {
        const set = new Set(albums.map((a) => a.category || 'General'));
        return ['All', ...Array.from(set).sort()];
    }, [albums]);

    const filteredAlbums = useMemo(() => {
        if (activeCategory === 'All') return albums;
        return albums.filter((a) => a.category === activeCategory);
    }, [albums, activeCategory]);

    const closeLightbox = () => setLightbox(null);
    const prevPhoto = () =>
        setLightbox((l) =>
            l ? { ...l, index: (l.index - 1 + l.photos.length) % l.photos.length } : l
        );
    const nextPhoto = () =>
        setLightbox((l) => (l ? { ...l, index: (l.index + 1) % l.photos.length } : l));

    // Keyboard nav for lightbox
    useEffect(() => {
        if (!lightbox) return;
        const handler = (e) => {
            if (e.key === 'ArrowLeft') prevPhoto();
            if (e.key === 'ArrowRight') nextPhoto();
            if (e.key === 'Escape') closeLightbox();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [lightbox]);

    return (
        <div data-testid="gallery-page">
            <PageHero
                eyebrow="Photo & Video Gallery"
                title="Moments that tell our story."
                subtitle="Event-wise albums from every corner of our work — placements, career yatras, health camps, green drives and disaster relief."
            />

            <section className="section-pad">
                <div className="container-page">
                    {/* Category tabs */}
                    {categories.length > 2 && (
                        <div
                            className="flex justify-center mb-8 overflow-x-auto"
                            data-testid="gallery-category-tabs"
                        >
                            <Tabs value={activeCategory} onValueChange={setActiveCategory}>
                                <TabsList className="bg-[hsl(var(--secondary))]/60 flex-wrap h-auto">
                                    {categories.map((c) => (
                                        <TabsTrigger
                                            key={c}
                                            value={c}
                                            data-testid={`gallery-category-${c.replace(/\s+/g, '-').toLowerCase()}`}
                                        >
                                            {c}
                                        </TabsTrigger>
                                    ))}
                                </TabsList>
                            </Tabs>
                        </div>
                    )}

                    {filteredAlbums.length === 0 ? (
                        <div className="text-center text-muted-foreground py-16">
                            No albums in this category — check back soon.
                        </div>
                    ) : (
                        <div
                            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
                            data-testid="gallery-albums-grid"
                        >
                            {filteredAlbums.map((a, i) => {
                                const photoCount = (a.photos || []).length;
                                const videoCount = (a.videos || []).length;
                                return (
                                    <motion.button
                                        key={a.id}
                                        type="button"
                                        onClick={() => setOpenAlbum(a)}
                                        initial={{ opacity: 0, y: 12 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.4, delay: i * 0.04 }}
                                        viewport={{ once: true, margin: '-40px' }}
                                        className="text-left"
                                        data-testid={`gallery-album-card-${a.id}`}
                                    >
                                        <Card className="h-full bg-card border-border card-lift overflow-hidden">
                                            <div className="aspect-[4/3] bg-muted overflow-hidden relative">
                                                {a.cover_image ? (
                                                    <img
                                                        src={a.cover_image}
                                                        alt={a.title}
                                                        loading="lazy"
                                                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[hsl(var(--primary))]/10 to-[hsl(var(--accent))]/10">
                                                        <Camera className="h-10 w-10 text-muted-foreground" />
                                                    </div>
                                                )}
                                                <div className="absolute top-3 right-3 flex gap-1.5">
                                                    {photoCount > 0 && (
                                                        <Badge
                                                            variant="secondary"
                                                            className="bg-white/90 text-[hsl(var(--primary))] backdrop-blur-sm font-semibold"
                                                        >
                                                            <ImagesIcon className="h-3 w-3 mr-1" />
                                                            {photoCount}
                                                        </Badge>
                                                    )}
                                                    {videoCount > 0 && (
                                                        <Badge
                                                            variant="secondary"
                                                            className="bg-[hsl(var(--accent))]/90 text-[hsl(var(--accent-foreground))] backdrop-blur-sm font-semibold"
                                                        >
                                                            <VideoIcon className="h-3 w-3 mr-1" />
                                                            {videoCount}
                                                        </Badge>
                                                    )}
                                                </div>
                                                {a.category && (
                                                    <div className="absolute bottom-3 left-3">
                                                        <Badge
                                                            variant="outline"
                                                            className="bg-white/90 backdrop-blur-sm text-[10px] border-white/60"
                                                        >
                                                            {a.category}
                                                        </Badge>
                                                    </div>
                                                )}
                                            </div>
                                            <CardContent className="p-5">
                                                <div className="font-display font-semibold text-lg leading-snug mb-1 line-clamp-2">
                                                    {a.title}
                                                </div>
                                                {a.caption && (
                                                    <div className="text-sm text-muted-foreground leading-relaxed line-clamp-2">
                                                        {a.caption}
                                                    </div>
                                                )}
                                            </CardContent>
                                        </Card>
                                    </motion.button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </section>

            {/* Album Dialog */}
            <Dialog
                open={!!openAlbum}
                onOpenChange={(o) => {
                    if (!o) {
                        setOpenAlbum(null);
                        setLightbox(null);
                    }
                }}
            >
                <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
                    <DialogHeader>
                        {openAlbum?.category && (
                            <Badge
                                variant="outline"
                                className="self-start mb-1 bg-[hsl(var(--accent))]/10 text-[hsl(30_90%_28%)] border-[hsl(var(--accent))]/40"
                            >
                                {openAlbum.category}
                            </Badge>
                        )}
                        <DialogTitle className="font-display text-2xl">
                            {openAlbum?.title}
                        </DialogTitle>
                        {openAlbum?.caption && (
                            <DialogDescription className="text-base text-foreground/75">
                                {openAlbum.caption}
                            </DialogDescription>
                        )}
                    </DialogHeader>

                    {/* Photos */}
                    {(openAlbum?.photos || []).length > 0 && (
                        <div className="mt-4">
                            <div className="text-xs font-semibold uppercase tracking-widest text-[hsl(var(--accent))] mb-3 inline-flex items-center gap-1.5">
                                <ImagesIcon className="h-3 w-3" /> Photos
                            </div>
                            <div
                                className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2"
                                data-testid="album-photos-grid"
                            >
                                {(openAlbum.photos || []).map((url, idx) => (
                                    <button
                                        key={`${url}-${idx}`}
                                        type="button"
                                        onClick={() =>
                                            setLightbox({
                                                photos: openAlbum.photos,
                                                index: idx,
                                            })
                                        }
                                        className="aspect-square bg-muted rounded-md overflow-hidden group"
                                        data-testid={`album-photo-${idx}`}
                                    >
                                        <img
                                            src={url}
                                            alt=""
                                            loading="lazy"
                                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.05]"
                                        />
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Videos */}
                    {(openAlbum?.videos || []).length > 0 && (
                        <div className="mt-6">
                            <div className="text-xs font-semibold uppercase tracking-widest text-[hsl(var(--accent))] mb-3 inline-flex items-center gap-1.5">
                                <VideoIcon className="h-3 w-3" /> Videos
                            </div>
                            <div
                                className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                                data-testid="album-videos-grid"
                            >
                                {(openAlbum.videos || []).map((v, idx) => (
                                    <div
                                        key={`${v.url}-${idx}`}
                                        className="rounded-lg overflow-hidden border border-border bg-black"
                                        data-testid={`album-video-${idx}`}
                                    >
                                        <div className="aspect-video bg-black">
                                            <iframe
                                                src={toEmbedUrl(v.url)}
                                                title={v.caption || `Video ${idx + 1}`}
                                                className="w-full h-full"
                                                frameBorder="0"
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                                allowFullScreen
                                                loading="lazy"
                                            />
                                        </div>
                                        {v.caption && (
                                            <div className="px-3 py-2 text-xs text-muted-foreground bg-card border-t border-border">
                                                {v.caption}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Photo Lightbox */}
            <Dialog
                open={!!lightbox}
                onOpenChange={(o) => !o && setLightbox(null)}
            >
                <DialogContent className="max-w-6xl p-0 bg-black/95 border-none">
                    <DialogHeader className="sr-only">
                        <DialogTitle>Photo</DialogTitle>
                    </DialogHeader>
                    <button
                        type="button"
                        onClick={closeLightbox}
                        className="absolute top-2 right-2 h-9 w-9 rounded-full bg-black/60 text-white hover:bg-black/80 flex items-center justify-center z-10"
                        aria-label="Close"
                        data-testid="lightbox-close"
                    >
                        <X className="h-4 w-4" />
                    </button>
                    {lightbox && lightbox.photos.length > 1 && (
                        <>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={prevPhoto}
                                className="absolute left-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/60 text-white hover:bg-black/80 hover:text-white z-10"
                                aria-label="Previous"
                                data-testid="lightbox-prev"
                            >
                                <ChevronLeft className="h-5 w-5" />
                            </Button>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={nextPhoto}
                                className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/60 text-white hover:bg-black/80 hover:text-white z-10"
                                aria-label="Next"
                                data-testid="lightbox-next"
                            >
                                <ChevronRight className="h-5 w-5" />
                            </Button>
                        </>
                    )}
                    <div className="flex items-center justify-center min-h-[70vh]">
                        {lightbox && (
                            <img
                                src={lightbox.photos[lightbox.index]}
                                alt=""
                                className="max-h-[85vh] max-w-full object-contain"
                            />
                        )}
                    </div>
                    {lightbox && lightbox.photos.length > 1 && (
                        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-white/80 text-xs bg-black/60 rounded-full px-3 py-1">
                            {lightbox.index + 1} / {lightbox.photos.length}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
